import React, { useState } from 'react';
import { Navigation } from 'lucide-react';
import type { TourStop } from '../../types/deliveryTour';
import { lineLabel, mapsUrl, qtyLabel } from '../../types/deliveryTour';
import {
  CompactVisitForm,
  SkipForm,
  SheetHeader,
  type CompactVisitPayload,
} from './CompactVisitForm';

type Mode = 'actions' | 'skip' | 'visit';

interface StopSheetProps {
  stop: TourStop;
  saving: boolean;
  tourCompleted?: boolean;
  onClose: () => void;
  onDelivered: () => void;
  onVisit: (payload: CompactVisitPayload) => void;
  onSkip: (reason: 'closed' | 'refused' | 'no_access' | 'other', note?: string) => void;
  onRetry: () => void;
  onRemove: () => void;
}

export const StopSheet: React.FC<StopSheetProps> = ({
  stop,
  saving,
  tourCompleted = false,
  onClose,
  onDelivered,
  onVisit,
  onSkip,
  onRetry,
  onRemove,
}) => {
  const [mode, setMode] = useState<Mode>('actions');
  const navi = mapsUrl(stop);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto p-4 space-y-4">
        <SheetHeader
          title={stop.customer_name}
          subtitle={stop.customer_address}
          onClose={onClose}
        />
        {stop.lieferschein_numbers.length > 0 ? (
          <p className="text-xs font-bold text-gray-400">
            {stop.lieferschein_numbers.join(' · ')}
          </p>
        ) : null}
        {stop.status === 'skipped' && stop.skip_reason ? (
          <p className="text-xs font-bold text-amber-700">
            Skipped: {stop.skip_reason}
          </p>
        ) : null}
        <ul className="space-y-1">
          {stop.items.map((item) => (
            <li key={item.order_id} className="text-sm">
              <span className="font-black">{qtyLabel(item)}</span> {lineLabel(item)}
            </li>
          ))}
        </ul>
        {navi !== '#' ? (
          <a
            href={navi}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-gray-900 text-white font-bold"
          >
            <Navigation size={16} /> Navigate
          </a>
        ) : null}

        {stop.legacy ? (
          <p className="text-sm text-gray-500">
            This is an older visit-based tour and is read-only.
          </p>
        ) : null}

        {mode === 'actions' &&
        stop.status === 'pending' &&
        !stop.legacy &&
        !tourCompleted ? (
          <div className="space-y-2">
            <button
              type="button"
              disabled={saving}
              onClick={onDelivered}
              className="w-full min-h-[48px] rounded-xl bg-green-600 text-white font-black disabled:opacity-50"
            >
              Delivered
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('visit')}
              className="w-full min-h-[48px] rounded-xl bg-brand-600 text-white font-black disabled:opacity-50"
            >
              Deliver + visit
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('skip')}
              className="w-full min-h-[48px] rounded-xl bg-gray-100 font-bold disabled:opacity-50"
            >
              Could not deliver
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={onRemove}
              className="w-full min-h-[48px] rounded-xl border-2 border-red-200 text-red-700 font-bold disabled:opacity-50"
            >
              Remove from tour
            </button>
          </div>
        ) : null}

        {mode === 'actions' &&
        stop.status === 'skipped' &&
        !stop.legacy &&
        !tourCompleted ? (
          <div className="space-y-2">
            <button
              type="button"
              disabled={saving}
              onClick={onRetry}
              className="w-full min-h-[48px] rounded-xl bg-brand-600 text-white font-black disabled:opacity-50"
            >
              Try again
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={onRemove}
              className="w-full min-h-[48px] rounded-xl border-2 border-red-200 text-red-700 font-bold disabled:opacity-50"
            >
              Remove from tour
            </button>
          </div>
        ) : null}

        {mode === 'skip' ? (
          <SkipForm
            saving={saving}
            onCancel={() => setMode('actions')}
            onSubmit={onSkip}
          />
        ) : null}

        {mode === 'visit' ? (
          <CompactVisitForm
            posId={stop.pos_id}
            items={stop.items}
            saving={saving}
            onCancel={() => setMode('actions')}
            onSubmit={onVisit}
          />
        ) : null}
      </div>
    </div>
  );
};
